import React from 'react';
import type { PageDetailOut } from '../../types/catalog';
import type { ContentBlock, PageAsset } from '../../types/page';
import { getVideoDisplayUrl, isGcsStorageUrl } from '../../utils/videoUrl';

interface StoryVideoWithPagesV1Props {
  page: PageDetailOut;
}

function getBlocks(page: PageDetailOut): ContentBlock[] {
  const content = page.page_json_v1?.content as { blocks?: ContentBlock[] } | undefined;
  return content?.blocks ?? [];
}

function getVideoAsset(page: PageDetailOut): PageAsset | null {
  const assets = (page.page_json_v1?.assets ?? []) as PageAsset[];
  return assets.find((a) => a.kind === 'video') ?? null;
}

export const StoryVideoWithPagesV1: React.FC<StoryVideoWithPagesV1Props> = ({ page }) => {
  const blocks = getBlocks(page);
  const videoAsset = getVideoAsset(page);
  const videoUrl = videoAsset?.url;
  const displayUrl = getVideoDisplayUrl(videoUrl);
  const isGcs = videoUrl ? isGcsStorageUrl(videoUrl) : false;

  const heading = blocks.find((b) => (b.type ?? '').toLowerCase() === 'heading');
  const paragraphs = blocks.filter((b) => (b.type ?? '').toLowerCase() === 'paragraph');

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
        {page.page_title ?? (page.page_json_v1 as { page_title?: string })?.page_title ?? 'Story'}
      </h1>

      {videoUrl && (
        <div className="space-y-2">
          <div className="rounded-xl overflow-hidden bg-black aspect-video">
            <video
              key={displayUrl}
              src={displayUrl}
              controls
              className="w-full h-full"
              playsInline
              preload="metadata"
            >
              Your browser does not support the video tag.
            </video>
          </div>
          {isGcs && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-200">
              Video may require public access. If it does not play,{' '}
              <a
                href={videoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium"
              >
                open video in new tab
              </a>
              .
            </div>
          )}
        </div>
      )}

      {heading?.text && (
        <h2 className="text-xl font-medium text-gray-800 dark:text-gray-200">{heading.text}</h2>
      )}

      {paragraphs.map((p, i) => (
        <p key={i} className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
          {p.text}
        </p>
      ))}

      {!videoUrl && blocks.length === 0 && (
        <p className="text-gray-500 dark:text-gray-400">No video or story content.</p>
      )}
    </div>
  );
};
